import type { Membership, Round, Sport } from '@h2h/shared';
import { ArrowRight, Calendar, LayoutGrid, Trophy, Zap } from '../../../components/ui/icons';
import { cn } from '../../../lib/cn';
import { formatCount, tierLabel } from '../../../lib/format';
import type { Tab } from '../../round-play/types';
import { hubLabels } from './hubLabels';

function hubHeadline(
  round: Round | null | undefined,
  sport: Sport,
  hasEntry: boolean,
  settled: boolean,
  live: boolean
): { title: string; body: string } {
  const cycling = sport === 'CYCLING';

  if (!round) {
    return {
      title: 'Waiting for the next slate',
      body: hubLabels(sport).emptySlateBody
    };
  }
  if (settled) {
    return {
      title: `${round.label} is settled`,
      body: 'See how you scored, who moved in your division, and get ready for the next slate.'
    };
  }
  if (live) {
    return {
      title: 'Slate is live',
      body: 'Your rank and points update as results land — check your division board below.'
    };
  }
  if (hasEntry) {
    return {
      title: cycling ? 'Your calls are in' : 'Your cards are saved',
      body: cycling
        ? 'Adjust before the stage locks, or follow live scoring in your division.'
        : 'Keep building cards until kickoff — every fixture you skip stays out of your score.'
    };
  }
  return {
    title: cycling ? 'Call your duels' : 'Build your cards',
    body: cycling
      ? 'Rank the six rider duels on this stage — your score stacks against rivals in your division.'
      : 'Build cards on any fixtures you fancy, stack boosts on each card, and climb your division.'
  };
}

/** Top-of-hub orientation: what to do, season context, and where to go next. */
export function LeagueHubGuide({
  sport,
  round,
  membership,
  hasEntry,
  rankInGroup,
  divisionSize,
  roundScore,
  onOpenTab,
  onStandings,
  onPicks
}: {
  sport: Sport;
  round: Round | null | undefined;
  membership: Membership | null | undefined;
  hasEntry: boolean;
  rankInGroup: number | null;
  divisionSize: number;
  roundScore: number;
  onOpenTab: (tab: Tab) => void;
  onStandings: () => void;
  onPicks: () => void;
}) {
  const labels = hubLabels(sport);
  const settled = round?.status === 'SETTLED';
  const live =
    round?.status === 'LOCKING' ||
    round?.status === 'SETTLING' ||
    (round?.status === 'OPEN' && hasEntry && roundScore > 0);

  const { title, body } = hubHeadline(round, sport, hasEntry, settled, live);
  const seasonPts = membership?.seasonPoints ?? 0;
  const roundsPlayed = membership?.roundsPlayed ?? 0;
  const promotions = membership?.promotions ?? 0;
  const tier = membership != null ? tierLabel(membership.tier).replace(' tier', '') : '—';

  const links = [
    { key: 'schedule', label: labels.scheduleLink, icon: Calendar, onClick: () => onOpenTab(labels.scheduleTabKey) },
    { key: 'table', label: labels.tableTab, icon: LayoutGrid, onClick: () => onOpenTab('table') },
    { key: 'standings', label: 'Standings', icon: Trophy, onClick: onStandings },
    { key: 'play', label: labels.playLink, icon: Zap, onClick: onPicks }
  ];

  return (
    <div className="mt-4 overflow-hidden rounded-[22px] border border-black/[0.06] bg-[linear-gradient(165deg,#FFFFFF,#F7F7FA)] shadow-[0_16px_36px_-28px_rgba(20,20,50,0.35)]">
      <div className="px-[16px] pb-[14px] pt-[15px]">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#0A9A72]">Your move</div>
        <div className="mt-1 text-[18px] font-extrabold tracking-[-0.03em] text-ink">{title}</div>
        <p className="mt-1.5 text-[12.5px] font-medium leading-[1.45] text-ink2">{body}</p>
      </div>

      <div className="grid grid-cols-4 gap-2 border-t border-black/[0.05] bg-white/80 px-[14px] py-3">
        <StatChip label="Season pts" value={formatCount(seasonPts)} accent />
        <StatChip label={labels.playedLabel} value={String(roundsPlayed)} />
        <StatChip label="Rank now" value={rankInGroup != null ? `${rankInGroup}/${divisionSize || '—'}` : '—'} />
        <StatChip label="Tier" value={tier} sub={promotions > 0 ? `${promotions}↑` : undefined} />
      </div>

      <div className="flex gap-2 overflow-x-auto border-t border-black/[0.05] px-[14px] py-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {links.map(({ key, label, icon: Icon, onClick }) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            className="tap flex shrink-0 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3.5 py-2 text-[12px] font-bold text-ink shadow-[0_4px_12px_-8px_rgba(0,0,0,0.25)] active:scale-[0.98]"
          >
            <Icon width={13} height={13} strokeWidth={2.2} className="text-ink2" />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatChip({
  label,
  value,
  sub,
  accent
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-[12px] bg-fill/80 px-2 py-2 text-center">
      <div className={cn('num truncate text-[15px] font-extrabold leading-none', accent ? 'text-accent-ink' : 'text-ink')}>
        {value}
      </div>
      {sub && <div className="num mt-0.5 text-[10px] font-extrabold text-accent-ink">{sub}</div>}
      <div className="mt-1 truncate text-[9px] font-bold uppercase tracking-[0.06em] text-ink3">{label}</div>
    </div>
  );
}

/** One-line hint under the hub segmented control. */
export function HubTabHint({ tab, sport }: { tab: Tab; sport: Sport }) {
  const labels = hubLabels(sport);
  const text =
    tab === 'compete'
      ? labels.competeHint
      : tab === 'fixtures' || tab === 'stages'
        ? labels.scheduleHint
        : tab === 'table'
          ? labels.tableHint
          : '';
  if (!text) return null;
  return <p className="mt-3 px-1 text-[12px] font-medium leading-[1.45] text-ink2">{text}</p>;
}

/** Compact jump back to the primary action from secondary tabs. */
export function HubTabBackToCompete({ sport, onClick }: { sport: Sport; onClick: () => void }) {
  const labels = hubLabels(sport);
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap mt-4 flex w-full items-center justify-between rounded-[16px] border border-black/[0.07] bg-white px-4 py-3.5 shadow-tile active:scale-[0.99]"
    >
      <div className="text-left">
        <div className="text-[13px] font-extrabold text-ink">Back to Compete</div>
        <div className="mt-0.5 text-[11px] font-medium text-ink2">{labels.competeBackBody}</div>
      </div>
      <ArrowRight width={16} height={16} strokeWidth={2.4} className="text-ink3" />
    </button>
  );
}
